// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {useCursorAtEnd} from '@app/features/app/hooks/useCursorAtEnd';
import styles from '@app/features/messaging/components/modals/AttachmentEditModal.module.css';
import type {Message} from '@app/features/messaging/models/Message';
import {conduit} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {Button} from '@app/features/ui/button/Button';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Form} from '@app/features/ui/components/form/Form';
import {Textarea} from '@app/features/ui/components/form/Input';
import {MAX_ATTACHMENT_ALT_TEXT_LENGTH} from '@app/vendor/constants/LimitConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useForm} from 'react-hook-form';

const logger = new Logger('EditAltTextModal');
interface FormInputs {
	description: string;
}
interface EditAltTextModalProps {
	message: Message;
	attachmentId: string;
	currentDescription?: string | null;
	snapshotIndex?: number;
	onClose: () => void;
}
export const EditAltTextModal = observer(
	({message, attachmentId, currentDescription, snapshotIndex, onClose}: EditAltTextModalProps) => {
		const {t} = useLingui();
		const [isSubmitting, setIsSubmitting] = useState(false);
		const textareaRef = useCursorAtEnd<HTMLTextAreaElement>();
		const form = useForm<FormInputs>({
			defaultValues: {
				description: currentDescription ?? '',
			},
		});
		const currentDescriptionValue = form.watch('description');
		const currentLength = useMemo(() => currentDescriptionValue.length, [currentDescriptionValue]);
		const isOverLimit = currentLength > MAX_ATTACHMENT_ALT_TEXT_LENGTH;
		const canSubmit = !isOverLimit && !isSubmitting;
		const onSubmit = useCallback(
			async (data: FormInputs) => {
				if (!canSubmit) return;
				setIsSubmitting(true);
				logger.debug(`Updating alt text for attachment ${attachmentId} in message ${message.id}`);
				try {
					if (snapshotIndex !== undefined) {
						const snapshots = message.messageSnapshots ?? [];
						const snapshotEdits = snapshots.map((snapshot, idx) => {
							if (idx !== snapshotIndex) return {};
							const attachments = (snapshot.attachments ?? []).map((att) => {
								if (att.id === attachmentId) {
									return {id: att.id, description: data.description || null};
								}
								return {id: att.id};
							});
							return {attachments};
						});
						await conduit.patch<Message>(Endpoints.CHANNEL_MESSAGE(message.channelId, message.id), {
							body: {
								message_snapshots: snapshotEdits,
							},
						});
					} else {
						const attachmentUpdates = message.attachments.map((att) => {
							if (att.id === attachmentId) {
								return {
									id: att.id,
									description: data.description || null,
								};
							}
							return {id: att.id};
						});
						await conduit.patch<Message>(Endpoints.CHANNEL_MESSAGE(message.channelId, message.id), {
							body: {
								content: message.content,
								attachments: attachmentUpdates,
							},
						});
					}
					logger.debug(`Successfully updated alt text for attachment ${attachmentId}`);
					ToastCommands.success(t`Alt text updated`);
					onClose();
				} catch (error) {
					logger.error('Failed to update alt text:', error);
					ToastCommands.error(t`Failed to update alt text`);
				} finally {
					setIsSubmitting(false);
				}
			},
			[canSubmit, attachmentId, message, onClose, t, snapshotIndex],
		);
		useEffect(() => {
			function handleKeyDown(event: KeyboardEvent) {
				if (event.key === 'Escape') {
					event.preventDefault();
					event.stopPropagation();
					onClose();
				} else if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
					event.preventDefault();
					event.stopPropagation();
					if (canSubmit) {
						void form.handleSubmit(onSubmit)();
					}
				}
			}
			document.addEventListener('keydown', handleKeyDown);
			return () => document.removeEventListener('keydown', handleKeyDown);
		}, [onClose, canSubmit, form, onSubmit]);
		const handleCancel = useCallback(() => {
			onClose();
		}, [onClose]);
		return (
			<Modal.Root size="small" centered onClose={onClose}>
				<Form form={form} onSubmit={onSubmit} aria-label={t`Edit alt text form`}>
					<Modal.Header title={t`Edit Alt Text`} onClose={onClose} />
					<Modal.Content className={styles.content}>
						<Textarea
							{...form.register('description')}
							ref={(el) => {
								textareaRef(el);
								form.register('description').ref(el);
							}}
							autoFocus={true}
							value={currentDescriptionValue}
							label={t`Alt Text Description`}
							placeholder={t`Describe this media for screen readers`}
							minRows={3}
							maxRows={8}
							showCharacterCount={true}
							maxLength={MAX_ATTACHMENT_ALT_TEXT_LENGTH}
							disabled={isSubmitting}
						/>
					</Modal.Content>
					<Modal.Footer>
						<Button onClick={handleCancel} variant="secondary" disabled={isSubmitting}>
							<Trans>Cancel</Trans>
						</Button>
						<Button type="submit" disabled={!canSubmit}>
							<Trans>Save</Trans>
						</Button>
					</Modal.Footer>
				</Form>
			</Modal.Root>
		);
	},
);
